import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Metro } from '../../types/metro';
import { RATIO_RANGE_BOUNDS, useFilterStore } from '../../stores/filterStore';
import { useMetroStore } from '../../stores/metroStore';
import { useFilteredMetros } from '../../hooks/useFilteredMetros';
import { EmptyState } from '../shared/EmptyState';
import { metroShortage } from '../../lib/utils';
import { MetroListItem } from './MetroListItem';
import { MapPanelHead } from './MapPanelHead';
import { MetroSearchCombobox } from './MetroSearchCombobox';
import { OccupationGroupCombobox } from '../shared/OccupationGroupCombobox';
import { ALL_OCCUPATIONAL_GROUPS, lensedRatioForMetro, slugifyGroupName } from '../../lib/occupation-utils';
import { RatioRangeSlider } from '../shared/RatioRangeSlider';
import { RatioMethodologyNote } from '../rankings/RatioMethodologyNote';
import { MapListHeader } from './MapListHeader';
import { DEFAULT_SORT_ASC, type SortKey } from './map-list-header-constants';
import { LeafletMap } from './LeafletMap';
import { MapLegend } from './MapLegend';
import { MetroPopup } from './MetroPopup';
import { TOPBAR_HEIGHT_MOBILE } from '../../lib/layout-offsets';

interface MapMobileLayoutProps {
  readonly metros: ReadonlyArray<Metro>;
  readonly selectedMetroId: string | null;
  readonly onSelectMetro: (id: string) => void;
}

/**
 * Mobile-only map layout (< 600px). Stacks the page vertically so
 * the map is usable on a narrow viewport — the desktop "absolute
 * sidebar overlaying a fullscreen map" pattern is unusable on mobile
 * because the 288px sidebar covers nearly the entire viewport.
 *
 * Stack (top → bottom):
 *   1. Filters block — panel head + metro search + occupation
 *      select + status chips + methodology pill. Scrolls with the
 *      page (no sticky chrome) so the user gets straight context
 *      without spending vertical real estate.
 *   2. LeafletMap — fixed at 50vh so the bubbles + tiles have
 *      enough room to be useful while leaving half the viewport
 *      for the list below.
 *   3. Scroll-hint banner — "Scroll down to explore metro areas"
 *      with a chevron, signalling to first-time mobile visitors
 *      that the metro list lives below the map (and is reachable
 *      by scrolling, not by interacting with the map alone).
 *   4. Metros list — `MapListHeader` (count + sort dropdown)
 *      followed by the standard `MetroListItem` rows in document
 *      flow so the user scrolls the whole page to browse them.
 *
 * Filter state pulls from the cross-page `useFilterStore`, identical
 * to `MapSidebar`. Sort state stays local (same UX as desktop).
 */
export function MapMobileLayout({
  metros,
  selectedMetroId,
  onSelectMetro,
}: MapMobileLayoutProps) {
  const search = useFilterStore((s) => s.search);
  const setSearch = useFilterStore((s) => s.setSearch);
  const ratioRange = useFilterStore((s) => s.ratioRange);
  const setRatioRange = useFilterStore((s) => s.setRatioRange);
  const occupationalGroupId = useFilterStore((s) => s.occupationalGroupId);
  const setOccupationalGroup = useFilterStore((s) => s.setOccupationalGroup);
  const reset = useFilterStore((s) => s.reset);

  const filtered = useFilteredMetros(metros);
  const [sortKey, setSortKey] = useState<SortKey>('ratio');
  const [sortAsc, setSortAsc] = useState<boolean>(true);

  // IMP-005 (mobile popup pass, 2026-06): on mobile the Leaflet popup
  // gets clipped inside the 50vh map container, so we render the
  // MetroPopup as a full-viewport modal instead, driven directly off
  // the selection store. We go through the store directly rather
  // than the onSelectMetro prop because the prop's
  // `(id: string) => void` signature can't express null/deselect.
  // (cleanup PR #82-cleanup, 2026-06-02): renamed
  // `deselectMetro` -> `setSelectedMetro` since the same setter is
  // used for select(id) AND deselect(null).
  const setSelectedMetro = useMetroStore((s) => s.selectMetro);
  const closeModal = useCallback(() => setSelectedMetro(null), [setSelectedMetro]);
  const selectedMetro = useMemo(
    () => metros.find((m) => m.id === selectedMetroId) ?? null,
    [metros, selectedMetroId],
  );
  const isModalOpen = selectedMetro !== null;

  // Refs for focus management + focus trap. `modalCardRef` is the
  // scoped focus boundary; `previousFocusRef` is what we return
  // focus to on close (typically the metro bubble / list item that
  // opened the modal).
  const modalCardRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Lock body scroll + listen for Escape while the popup modal is up
  // so the page underneath can't be panned and the user has a
  // keyboard-discoverable dismiss path. Effect is no-op when the
  // modal isn't open.
  //
  // Cleanup PR #82-cleanup (2026-06-02): deps now key off
  // `selectedMetroId` (stable string) instead of `selectedMetro`
  // (object from useMemo over `metros`). When the metros array
  // ref changes from a data refresh, the useMemo recomputes and
  // returns a new object reference even when the SELECTION hasn't
  // changed -- the old deps caused the effect to tear down +
  // re-apply the body-scroll lock unnecessarily and leak the
  // cached `previousOverflow`.
  useEffect(() => {
    if (!isModalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey);
    };
  }, [isModalOpen, closeModal]);

  // Focus management. On open: capture the element that triggered
  // the modal (so we can restore focus on close), then move focus
  // into the modal card so keyboard users land inside the dialog.
  // On close: restore focus to the trigger so the user's tab order
  // resumes from where they were. WCAG 2.4.3 (focus order) + the
  // standard APG dialog pattern.
  //
  // Implementation note: we focus the modal CARD (not the close
  // button) so the visible affordance the user sees first is the
  // metro name + table, not the small X button -- they can still
  // Tab to the X immediately afterward.
  useEffect(() => {
    if (!isModalOpen) return undefined;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    // Defer to next frame so the card has mounted and its tabIndex
    // is honored by .focus().
    const handle = window.requestAnimationFrame(() => {
      modalCardRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(handle);
      // Restore focus only if the modal is actually closing (not
      // just a re-render). The previousFocusRef may have been
      // unmounted while the modal was open (e.g. data refresh)
      // -- guard with `isConnected`.
      const prev = previousFocusRef.current;
      if (prev && prev.isConnected && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [isModalOpen]);

  // Minimal focus trap. Tab / Shift-Tab inside the modal cycles
  // through focusable descendants of the card; if the user tries
  // to leave the boundary, we wrap. We listen on the card itself
  // (capture phase via React's onKeyDown) so we don't fight with
  // every other keydown listener on the page.
  const handleModalKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const root = modalCardRef.current;
    if (!root) return;
    const focusables = root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement as HTMLElement | null;
    // Shift+Tab from the first focusable -> wrap to last.
    if (e.shiftKey && (active === first || active === root)) {
      e.preventDefault();
      last.focus();
      return;
    }
    // Tab from the last focusable -> wrap to first.
    if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  /** Picking a new sort key resets direction to that key's sensible
   *  default (DEFAULT_SORT_ASC, shared with MapSidebar) rather than
   *  carrying over whatever direction the previous key was in
   *  (CR-05, 2026-07-08 -- Maria 3e). */
  const handleSortKeyChange = (key: SortKey) => {
    setSortKey(key);
    setSortAsc(DEFAULT_SORT_ASC[key]);
  };
  const toggleSortDirection = () => setSortAsc((asc) => !asc);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      let cmp: number;
      switch (sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'credentials':
          cmp = a.totalCredentials - b.totalCredentials;
          break;
        case 'jobs':
          cmp = a.totalJobs - b.totalJobs;
          break;
        case 'shortage':
          cmp = metroShortage(a) - metroShortage(b);
          break;
        case 'ratio':
        default:
          // CR-039: sort on the figure the row DISPLAYS — the lensed
          // ratio under an OG filter (falls back to metro-level when
          // no lens is active / the metro lacks the group).
          cmp =
            lensedRatioForMetro(a, occupationalGroupId).ratio -
            lensedRatioForMetro(b, occupationalGroupId).ratio;
      }
      return sortAsc ? cmp : -cmp;
    });
    return list;
  }, [filtered, sortKey, sortAsc, occupationalGroupId]);

  return (
    <>
    <div className="flex flex-col">
      {/* Above-the-fold viewport block: filters + map + legend +
          scroll-hint together fill exactly `100dvh - 68px` (viewport
          minus the sticky 68px mobile topbar, CR-028/029 -- this
          component only renders <600px, where the Topbar's mobile
          height applies, not the desktop 74px). The map uses `flex-1` to
          absorb whatever vertical space remains after the other rows
          take their natural heights, so the scroll-hint banner always
          sits at the bottom of the visible screen regardless of how
          tall the filters block ends up.

          We use `100dvh` (dynamic viewport height) rather than `100vh`
          so iOS Safari's collapsing address bar doesn't push the
          scroll-hint banner below the visible fold. */}
      <div
        className="flex flex-col"
        style={{ height: `calc(100dvh - ${TOPBAR_HEIGHT_MOBILE}px)` }}
      >
        {/* 1. Filters block — natural height, doesn't flex. */}
        <section aria-label="Metro filters" className="flex-none bg-white">
          <MapPanelHead />
          <MetroSearchCombobox metros={metros} value={search} onChange={setSearch} />
          <div className="border-b border-border px-5 pb-[14px] pt-3">
            {/* PR B (combobox plan, 2026-06-01): typeahead combobox
                matches desktop sidebar + Detail/Rankings/Providers. */}
            <div className="mb-[14px]">
              <OccupationGroupCombobox
                value={occupationalGroupId ?? ''}
                onChange={(id) => setOccupationalGroup(id || null)}
                groups={ALL_OCCUPATIONAL_GROUPS.map((name) => ({
                  id: slugifyGroupName(name),
                  name,
                  color: '#0074CC',
                }))}
                label="Occupational Group"
              />
            </div>
            {/* 2026-07-02 (Georgetown feedback): shortage/surplus chips
                replaced by the ratio slider — see MapSidebar for the
                design rationale. Mobile shell uses the same component
                and store slice so filters stay coherent across the two
                viewports. */}
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-text-4">
                Credentials-to-Jobs Ratio
              </p>
              {(ratioRange[0] !== RATIO_RANGE_BOUNDS[0] ||
                ratioRange[1] !== RATIO_RANGE_BOUNDS[1]) && (
                <button
                  type="button"
                  onClick={() => setRatioRange(RATIO_RANGE_BOUNDS)}
                  className="text-[10px] font-semibold text-blue-link hover:underline"
                >
                  Reset
                </button>
              )}
            </div>
            <div className="mt-1">
              <RatioRangeSlider
                min={RATIO_RANGE_BOUNDS[0]}
                max={RATIO_RANGE_BOUNDS[1]}
                step={0.05}
                value={ratioRange}
                onChange={setRatioRange}
                ariaLabel="Filter metros by credentials-to-jobs ratio"
                inflection={1.0}
              />
            </div>
          </div>
          <div className="px-5 pb-3 pt-2">
            <RatioMethodologyNote variant="compact" />
          </div>
        </section>

        {/* 2. Map — flexes to fill whatever vertical space is left
            after the filters, legend, and scroll-hint claim theirs.
            `min-h-[160px]` keeps it usable even when filters are tall
            on a short viewport (e.g. landscape phones). */}
        <div
          className="relative w-full flex-1 min-h-[160px] overflow-hidden border-y border-border"
          aria-label="Map"
        >
          <LeafletMap
            metros={filtered}
            selectedMetroId={selectedMetroId}
            onSelectMetro={onSelectMetro}
          />
        </div>

        {/* 3. Map legend — natural height, doesn't flex. */}
        <MapLegend variant="inline" />

        {/* 4. Scroll hint — sits at the bottom of the viewport,
            telling the user the metro list lives below the fold. */}
        <div className="flex flex-none items-center justify-center gap-2 border-b border-border bg-ice py-3 text-[12px] font-semibold uppercase tracking-[0.04em] text-navy">
          <span>Scroll down to explore metro areas</span>
          <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {/* 5. Metros list — below the fold, reached by scrolling. */}
      <MapListHeader
        count={sorted.length}
        sortKey={sortKey}
        sortAsc={sortAsc}
        onSortKeyChange={handleSortKeyChange}
        onToggleDirection={toggleSortDirection}
      />
      {sorted.length === 0 ? (
        <div className="px-5 py-6">
          <EmptyState
            title="No metros match"
            description="Try widening the ratio range or clearing the search."
            action={
              <button
                type="button"
                onClick={reset}
                className="text-xs font-semibold text-blue-link hover:underline"
              >
                Clear filters
              </button>
            }
          />
        </div>
      ) : (
        <ul aria-label="Metro list">
          {sorted.map((metro) => (
            <li key={metro.id}>
              <MetroListItem
                metro={metro}
                isSelected={metro.id === selectedMetroId}
                onSelect={onSelectMetro}
                occupationalGroupId={occupationalGroupId}
              />
            </li>
          ))}
        </ul>
      )}
    </div>

    {/* Mobile MetroPopup modal — full-viewport overlay that takes
        over when the user clicks a metro bubble or list item. The
        Leaflet popup is suppressed on mobile (see MetroBubble), so
        this modal is the only metro-detail surface on a narrow
        viewport. Reuses the same MetroPopup body as the desktop
        popup so the per-group table, sort affordance, and
        quick-nav links stay consistent across breakpoints.

        z-index sits above the Topbar (z-40) and the mobile menu
        drawer (z-[1100] when open) is mutually exclusive with this
        modal — the user can't have a bubble selected while the
        nav drawer is open since selecting one clears the other
        via navigation. */}
    {selectedMetro && (
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${selectedMetro.name} details`}
        className="fixed inset-0 z-[5000] flex items-center justify-center bg-navy/40 backdrop-blur-[2px] px-3 py-8"
      >
        {/* Backdrop — clicking outside the card closes the modal.
            Cleanup PR #82-cleanup (2026-06-02): rendered as a
            decorative `<div aria-hidden="true">` (same pattern as
            IntroModal) rather than a `<button aria-label="Close
            popup">`. The previous button shared its accessible
            name with the inner MetroPopup X button inside the
            same dialog -- two "Close popup, button" announcements
            for assistive tech in a 4-button modal. Backdrop dismiss
            is a visual / mouse affordance only; keyboard users
            close via Escape or the inner X. */}
        <div
          aria-hidden="true"
          onClick={closeModal}
          className="absolute inset-0 cursor-pointer"
        />

        {/* Modal card. max-w caps the popup width on tablets
            (560px matches the desktop popup body). We use a
            DEFINITE height (`h-[calc(100dvh-64px)]`, not max-h)
            so the inner CSS Grid's `1fr` middle row has real
            space to occupy and the table can actually shrink to
            fit + scroll inside it. The `py-8` on the backdrop
            spares 32px above and below.

            The card does NOT scroll itself; MetroPopup renders
            three rows (header / table / nav) where only the
            middle row scrolls. `overflow-hidden` clips the
            rounded corners over the inner scroll area.

            Cleanup PR #82-cleanup (2026-06-02): card now carries
            a ref + `tabIndex={-1}` so the open-modal effect can
            move focus into it, and an onKeyDown handler that
            traps Tab/Shift-Tab inside the focusable descendants.
            `outline-none` because the focus ring on the CARD
            itself is visually redundant -- the user lands here
            then immediately Tabs to the close button, which has
            its own visible ring. */}
        <div
          ref={modalCardRef}
          tabIndex={-1}
          onKeyDown={handleModalKeyDown}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-popup h-[calc(100dvh_-_var(--modal-inset))] overflow-hidden rounded-lg bg-white shadow-[0_20px_60px_-10px_rgba(20,65,117,0.5)] outline-none"
        >
          <MetroPopup
            metro={selectedMetro}
            onClose={closeModal}
            surface="modal"
          />
        </div>
      </div>
    )}
    </>
  );
}
